import { useClerk, useUser } from '@clerk/react-router';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsClient } from 'usehooks-ts';

import { AUTHENTICATE_AS } from '@/lib/constants';
import useRouteType from '@/lib/hooks/use-route-type';

import { LoadingBlockFullScreen } from '../loading/loading-block-full-screen';

import { RedirectToSignIn } from '@clerk/react-router';
import { useAppStateMachine } from './app-state-machine-provider';

export const isClerkDisabled = !!AUTHENTICATE_AS;

interface Props {
    children: React.ReactNode | React.ReactNode[];
}

export const AppStateRedirectProvider: React.FC<Props> = ({ children }) => {
    const { isSuperAdminRoute, isPublicRoute } = useRouteType();
    const { state, send } = useAppStateMachine();
    const [lastState, setLastState] = useState(state.value);
    const navigate = useNavigate();
    const { user, isLoaded } = useUser();
    const { signOut } = useClerk();
    const isClient = useIsClient();

    const renderLoading = () => {
        return <LoadingBlockFullScreen />;
    };

    useEffect(() => {
        if ((user || isClerkDisabled) && state.matches('NOT_AUTHENTICATED')) {
            send({ type: 'USER_LOGGED_IN' });
        }
    }, [user, isClerkDisabled]);

    useEffect(() => {
        if (process.env.NODE_ENV === 'development') {
            console.log('App state changed to: ', state.value);
        }
        lastState !== state.value && setLastState(state.value);
    }, [state]);

    if (isPublicRoute) {
        // If user is authenticated on a public route (like /sign-in), redirect them
        if (state.matches('SUPER_ADMIN')) {
            navigate('/admin');
            return renderLoading();
        } else if (state.matches('PLATO_USER') || state.matches('IMPOERSONATION_SESSION')) {
            navigate('/');
            return renderLoading();
        }
        return <>{children}</>;
    } else if (!isClient) return renderLoading();
    else if (state.matches('LOGGING_OUT_OF_CLERK')) {
        signOut().then(() => {
            send({ type: 'DID_LOGOUT_OF_CLERK' });
            navigate('/sign-in');
        });
        return renderLoading();
    } else if (state.matches('NOT_AUTHENTICATED')) {
        // Only redirect if Clerk is loaded AND there's no user
        // If user exists, wait for state machine to transition
        if (isLoaded && !user && !isClerkDisabled) {
            return <RedirectToSignIn />;
        }
        return renderLoading();
    } else if (
        [
            'NOT_AUTHENTICATED',
            'LOADED_CLERK',
            'LOADING_PLATO_USER',
            'LOADING_SUPER_ADMIN',
            'LOADING_IMPOERSONATION_SESSION',
            'LOGGING_OUT_OF_CLERK',
            'CHECK_SUPER_ADMIN_WAS_PREVIOUSLY_IMPERSONATING',
        ].includes(state.value as string)
    ) {
        return renderLoading();
    } else if (state.matches('NO_ACCOUNT')) {
        throw new Error('No account');
    } else if (isSuperAdminRoute && state.matches('IMPOERSONATION_SESSION')) {
        navigate('/');
        return renderLoading();
    } else if (isSuperAdminRoute && !state.matches('SUPER_ADMIN')) {
        navigate('/');
        return renderLoading();
    } else if (!isSuperAdminRoute && state.matches('SUPER_ADMIN')) {
        // We have the last state with a tenant ID
        navigate(`/admin/customers/${state.context.tenantId ?? ''}`, {
            replace: true,
        });
        return renderLoading();
    } else {
        return <>{children}</>;
    }
};
